import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    throwError as observableThrowError,
} from "rxjs"

import { HttpParams } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { catchError, map } from "rxjs/operators"
import { CHNode } from "../models/node.model"
import { Subjects } from "../models/subject.model"
import { LabeledValue } from "../utils/types"
import Utils from "../utils/utils"
import { LmApiService } from "./lm-api.service"
import { UserService } from "./user.service"
import { AddOnViewService } from "./add-on-view.service"
import { ORDERED_MEDIA_TYPES } from "../models/constants"

@Injectable()
export class BrowsingService {
    public mediaTypes = ORDERED_MEDIA_TYPES

    public sortByOptions: LabeledValue[] = [
        { value: "relevance", label: "Relevance" },
        { value: "popularity", label: "Popularity" },
        { value: "recency", label: "Newest" },
    ]
    public activeNodeChanged: BehaviorSubject<CHNode> = new BehaviorSubject<CHNode>(CHNode.empty())
    public invalidNodeSelected = new Subject<any>()
    public breadcrumbs: CHNode[] = []
    public tree = new ReplaySubject<CHNode[]>()
    public autocompleteSubjects = new ReplaySubject<Record<string, CHNode>>()
    private lastActiveNode: CHNode
    private treeMaxDepth: number = -1
    private loadedTreeMaxDepth: number = -1

    // TODO: The main observers need to be refactored for clarity + loading data only when needed
    constructor(
        private lmApi: LmApiService,
        private userService: UserService,
        private addOnService: AddOnViewService
    ) {}

    public getSubjectsTree(maxDepth: number = 4): ReplaySubject<CHNode[]> {
        if (this.treeMaxDepth !== maxDepth) {
            this.treeMaxDepth = maxDepth
            this.lmApi
                .getWithCookiesInParams(`/api/v2/expanded_subject_tree/?max_depth=${maxDepth}`)
                .pipe(
                    catchError((error) => observableThrowError(error)),
                    map((data: any) => {
                        if (
                            this.userService.hasStudentExperience() ||
                            this.addOnService.isAddonView
                        ) {
                            data.objects = data.objects.filter(
                                (node) => node.title !== "Professional Development"
                            )
                        }
                        return data
                    })
                )
                .subscribe((data: any) => {
                    this.loadedTreeMaxDepth = maxDepth
                    this.tree.next(data.objects.map((nodeObj) => CHNode.fromObject(nodeObj)))
                    this.autocompleteSubjects.next(CHNode.nodesByTitle)
                })
        }
        return this.tree
    }

    public getNode(nodeId: number) {
        return this.getSubjectsTree().pipe(
            map((data) => {
                return CHNode.byID(nodeId)
            })
        )
    }

    public selectNode(nodeSlug: string) {
        nodeSlug = CHNode.cleanSlug(nodeSlug)
        return this.getSubjectsTree().subscribe(
            (tree: CHNode[]) => {
                const node: CHNode = CHNode.bySlug(nodeSlug)
                if (!node) {
                    if (this.loadedTreeMaxDepth == 4) {
                        this.invalidNodeSelected.next({ slug: nodeSlug })
                    }
                    // Do not emit an invalid node selection yet,
                    // we still are waiting for the whole tree to load
                    return
                }
                this.lastActiveNode = node
                this.activeNodeChanged.next(node)
            },
            (error) => console.error(`[CUSTOM Error]${error.message}`)
        )
    }

    public emitLastNodeSection() {
        this.activeNodeChanged.next(this.lastActiveNode)
    }

    public getResults(idOrSlug, filters) {
        const observablesArray = []
        const mediaType = this.hasFiltersMediaType(filters)

        if (mediaType) {
            const joinedType = mediaType.replace(/\s/g, "+")
            observablesArray.push(this.getResultsByMedia(idOrSlug, joinedType, filters))
        } else {
            this.mediaTypes.forEach((type) => {
                const joinedType = type.replace(/\s/g, "+")
                observablesArray.push(this.getResultsByMedia(idOrSlug, joinedType, filters))
            })
        }
        return observablesArray
    }

    public getResultsByMedia(idOrSlug, mediaType, filters, grades = null) {
        let params = new HttpParams()
        params = params.append("q", "")
        if (idOrSlug) {
            params = params.append("selected_facet", `subject:${idOrSlug}`)
        }
        if (!this.hasFiltersMediaType(filters)) {
            params = params.append("selected_facet", `media_type:${mediaType}`)
        }

        if (filters.hasOwnProperty("selected_facet")) {
            if (Array.isArray(filters.selected_facet)) {
                filters.selected_facet.map((filter) => {
                    params = params.append("selected_facet", filter)
                })
            } else {
                params = params.append("selected_facet", filters.selected_facet)
            }
        }
        if (grades) {
            // If grades are already added as a user-entered facet, this will only narrow the results
            params = params.append("selected_facet", `grades:${grades}`)
        }

        if (filters.hasOwnProperty("rank_by")) {
            const rankByType: string = filters.rank_by
            if (rankByType === "recency" || rankByType === "popularity") {
                params = params.append("rank_by", rankByType)
            }
        }

        if (this.userService.hasStudentExperience() || this.addOnService.isAddonView) {
            params = params.append("selected_facet", "student_content:true")
        }

        if (this.addOnService.isAddonView) {
            params = params.append(
                "excluded_facet",
                "media_type:Collection,Lesson Plan,Interactive"
            )
        }

        return this.lmApi
            .getWithCookiesInParamsAll("/api/v2/search/", {
                params,
            })
            .pipe(
                map((response: any) => {
                    response.meta.mediaType = mediaType
                    return response
                }),
                catchError((error) => observableThrowError(error))
            )
    }

    public getMoreResults(url) {
        return this.lmApi
            .getWithCookiesInParamsAll(url)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public getGradeBrowseResults(filters, grades) {
        const observablesArray = []
        const mediaType = this.hasFiltersMediaType(filters)
        if (mediaType) {
            const joinedType = mediaType.replace(/\s/g, "+")
            observablesArray.push(this.getResultsByMedia(null, joinedType, filters, grades))
        } else {
            this.mediaTypes.forEach((type) => {
                const joinedType = type.replace(/\s/g, "+")
                observablesArray.push(this.getResultsByMedia(null, joinedType, filters, grades))
            })
        }
        return observablesArray
    }

    public getSubjectsForErrorPage(): Observable<CHNode[]> {
        this.getSubjectsTree()
        return this.tree.pipe(
            map(
                (data: CHNode[]) => {
                    return data
                        .map((n) => {
                            if (n.id === Subjects.TheArts) {
                                n.title = "Arts"
                            }
                            return n
                        })
                        .sort(Utils.getCompareFnByKey("title"))
                        .map((n) => {
                            if (n.id === Subjects.TheArts) {
                                n.title = "The Arts"
                            }

                            return n
                        })
                },
                (error) => console.error(`[CUSTOM Error]${error}`)
            )
        )
    }

    private hasFiltersMediaType(filters: any) {
        let mediaType = null
        if (filters.selected_facet) {
            if (Array.isArray(filters.selected_facet)) {
                filters.selected_facet.forEach((facet) => {
                    if (facet.indexOf("media_type:") !== -1) {
                        mediaType = facet.split(":")[1]
                    }
                })
            } else {
                mediaType =
                    filters.selected_facet.indexOf("media_type:") !== -1
                        ? (mediaType = filters.selected_facet.split(":")[1])
                        : null
            }
        }
        return mediaType
    }
}
